import { AspectRatio, Box, Flex, Text, FlexProps, useTheme } from '@chakra-ui/react';
import React, { useState } from 'react';
import { PriceTag, ImageWithFallback } from 'src/components/atoms';
import { Listing } from 'src/api/v1-api';
import NextLink from 'next/link';
import { categoryTypeHasPrice } from 'src/utils/categoryUtils';
import { getListingImage, onImageError } from 'src/utils/listingUtils';

interface Props {
  listing: Listing;
  flexProps?: FlexProps;
  columns?: number;
}

const CompactListingCard = ({ listing, flexProps, columns }: Props): JSX.Element => {
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);

  const featuredImage = getListingImage(listing?.listing_images) ?? '';
  const thumbnailImage = listing?.listing_images?.[0]?.thumbnail ?? '';

  if (imageError) {
    return <></>;
  }

  return (
    <NextLink href={`/listing/${listing.id}`} style={{ display: 'flex', flexGrow: 1 }}>
      <Flex
        direction="column"
        gap={3}
        align="flex-start"
        justify="flex-start"
        style={{ flexGrow: 1, minWidth: 0 }}
        {...flexProps}
        data-test="compact-listing-card"
        data-test-id={`${listing.id}`}
      >
        <Box position="relative" width="100%">
          <AspectRatio ratio={1}>
            <Box
              borderRadius={{ base: theme.border_radius.border_radius_1 }}
              overflow="hidden"
              position="relative"
            >
              {thumbnailImage ? (
                <img
                  key={`${listing.id}_thumb`}
                  src={thumbnailImage}
                  alt={listing?.title ?? 'Listing Image'}
                />
              ) : (
                <ImageWithFallback
                  key={`${listing.id}_${featuredImage}`}
                  src={featuredImage}
                  columns={columns ?? 2}
                  alt={listing?.title ?? 'Listing Image'}
                  onError={onImageError(listing, setImageError)}
                />
              )}
            </Box>
          </AspectRatio>
        </Box>

        <Flex direction="column" gap={2} align="flex-start">
          <Text
            fontWeight="500"
            noOfLines={2}
            fontFamily={theme.fonts.font_2}
            fontSize="14px"
            lineHeight="1.4"
            minHeight="38px"
            data-test="compact-card-listing-title"
          >
            {listing.title}
          </Text>

          {categoryTypeHasPrice(listing?.category_type) &&
            (listing.lowest_regular_price || listing.lowest_offered_price) && (
              <Box>
                <PriceTag
                  regularPrice={listing.lowest_regular_price}
                  offeredPrice={listing.lowest_offered_price}
                  currency={listing.currency}
                  regularPriceProps={{
                    fontSize: 16,
                    fontWeight: 700,
                    fontFamily: theme.fonts.font_2,
                  }}
                  offeredPriceProps={{
                    fontSize: 16,
                    fontWeight: 700,
                    fontFamily: theme.fonts.font_2,
                  }}
                />
              </Box>
            )}
        </Flex>
      </Flex>
    </NextLink>
  );
};

export default CompactListingCard;
